import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { scheduledMessagesQuery } from 'components/domain/automation/scheduledMessages/scheduledMessagesQuery';
import useFilterFormContext from 'components/filters/useFilterFormContext';
import CommonTable from 'components/table/CommonTable';
import FormattedDateCell from 'components/table/cells/common/FormattedDateCell';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import FormattedDateWithTimezone from 'pages/automations/common/cells/FormattedDateWithTimezone';
import TriggerActionTypeCell from 'pages/automations/common/cells/TriggerActionTypeCell';
import LeadCell from 'pages/automations/common/cells/lead/LeadCell';
import PropertyCell from 'pages/automations/common/cells/property/PropertyCell';
import { ScheduledMessagesTabFiltersSchemaType } from '../filters/ScheduledMessagesTabFilters.schema';
import useSearchedScheduledMessage from '../filters/useSearchedScheduledMessage';
import ActionsCell from './cells/ActionsCell';
import StateCell from './cells/StateCell';

const ScheduledMessagesTable = () => {
  const { t } = useTranslation();
  const searchTerm = useSearchedScheduledMessage();
  const { lastSubmittedFilters } =
    useFilterFormContext<ScheduledMessagesTabFiltersSchemaType>();
  const columnHelper = createColumnHelper<ScheduledMessage>();

  const columns = [
    columnHelper.accessor('expectedFireTime', {
      header: t('pageAutomations.scheduledMessages.table.expectedFireTime'),
      cell: FormattedDateCell,
      sortDescFirst: true,
      meta: {
        dateFormat: 'PPp',
        cellTooltipText: FormattedDateWithTimezone,
      },
    }),
    columnHelper.accessor('messageName', {
      header: t('pageAutomations.scheduledMessages.table.messageName'),
      sortDescFirst: true,
    }),
    columnHelper.accessor('action', {
      header: t('pageAutomations.scheduledMessages.table.action'),
      cell: TriggerActionTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('leadUid', {
      header: t('pageAutomations.scheduledMessages.table.leadUid'),
      cell: LeadCell,
      enableSorting: false,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('propertyUid', {
      header: t('pageAutomations.scheduledMessages.table.propertyUid'),
      cell: PropertyCell,
      enableSorting: false,
    }),
    columnHelper.accessor('state', {
      header: t('pageAutomations.scheduledMessages.table.state'),
      cell: StateCell,
      enableSorting: false,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { tableInstance, isLoading } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      scheduledMessagesQuery({
        filters: { ...lastSubmittedFilters },
        searchTerm,
        ...options,
      }),
    pageSize: 10,
  });

  return (
    <CommonTable
      data-testid="scheduled-messages-table"
      emptyState={t('pageAutomations.scheduledMessages.emptyTable')}
      isLoading={isLoading}
      tableInstance={tableInstance}
    />
  );
};

export default ScheduledMessagesTable;
